import type { LanguageDefinitionResponse } from '@onefootprint/request-types/dashboard';
import type * as monaco from 'monaco-editor';
import { hoverProvider } from '@/components/expression-editor/hover-provider';
import { getCompletionItems } from './utils/get-completion-items';

export function registerLanguage(monacoInstance: typeof monaco, languageSpec: LanguageDefinitionResponse) {
  const languageName = languageSpec.languageName;

  if (monacoInstance.languages.getLanguages().some(lang => lang.id === languageName)) {
    return;
  }

  const operators = [...new Set(Object.values(languageSpec.operatorMap).flat())] as string[];

  monacoInstance.languages.register({ id: languageName });

  monacoInstance.languages.setLanguageConfiguration(languageName, {
    // Set the word pattern to something that will never match.
    // This makes Monaco accept all characters as trigger characters.
    wordPattern: /(?=a)[^a]/,

    // Auto-close and highlight bracket pairs.
    brackets: [
      ['"', '"'],
      ["'", "'"],
      ['[', ']'],
      ['(', ')'],
    ],
    // Don't colorize bracket pairs. Just use regular syntax highlighting.
    colorizedBracketPairs: [],
  });

  // See docs: https://microsoft.github.io/monaco-editor/monarch.html
  monacoInstance.languages.setMonarchTokensProvider(languageName, {
    operator: operators,
    keyword: languageSpec.keywords,

    boolean: ['true', 'false'],

    // Escaped strings. Taken from https://microsoft.github.io/monaco-editor/monarch.html.
    escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,

    symbolOperator: /[=><!~?:&|+\-*/^%]+/,

    tokenizer: {
      root: [
        // Symbolic Operators
        [
          /@symbolOperator/,
          {
            cases: {
              '@operator': 'operator',
            },
          },
        ],

        // Word operators (e.g. "in", "not in")
        ...operators.filter(op => /^[a-z ]+$/.test(op)).map(op => [new RegExp(`\\b${op}\\b`), 'operator']),

        // Namespaces and keywords
        [
          /[a-zA-Z][a-zA-Z0-9_.$]*/,
          {
            cases: {
              '@keyword': 'keyword',
              '@boolean': 'boolean',
              '@default': 'identifier',
            },
          },
        ],

        [/\d+/, 'number'],

        // Strings
        [/"([^"\\]|\\.)*$/, 'string.invalid'], // non-teminated string
        [/"/, { token: 'string.quote', bracket: '@open', next: '@string' }],

        // Other characters
        [/'[^\\']'/, 'string'],
        [/(')(@escapes)(')/, ['string', 'string.escape', 'string']],
        [/'/, 'string.invalid'],
      ],

      string: [
        [/[^\\"]+/, 'string'],
        [/@escapes/, 'string.escape'],
        [/\\./, 'string.escape.invalid'],
        [/"/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
      ],
    },
  } as monaco.languages.IMonarchLanguage);

  monacoInstance.languages.registerHoverProvider(languageName, hoverProvider(monacoInstance, languageSpec, []));

  // Trigger on any character. Monaco doesn't trigger inside word patterns, but we set the wordPattern above to something that will never match to make autocomplete maximally eager.
  const triggerCharacters = Array.from({ length: 256 }).map((_, index) => String.fromCharCode(index));

  monacoInstance.languages.registerCompletionItemProvider(languageName, {
    triggerCharacters,
    provideCompletionItems: (model, position) => {
      const ret = getCompletionItems(monacoInstance, model, position, languageSpec);
      return ret;
    },
  });
}
